import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { Table, Input, Empty, Flex } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
const { Search } = Input;

import BaseCard from "../../BaseCard";
import { fetchInstrumentByCode } from "../../../utils/fetchers";
import { SkeletonTableTabs } from "../../skeletons/instruments";
import { InstrumentContext } from "../../../providers/InstrumentContextProvider";
import useLocale from "../../../hooks/useLocale";
import { __ } from "../../../locales/translation";

import renderColumns from "./renderColumns";
export default function ProductTable({ instumentCode, instrumentProductPageUrl }) {
    const navigate = useNavigate();
    const locale = useLocale();
    const { instrumentName } = useContext(InstrumentContext);
    const queryClient = useQueryClient();
    const {
        isLoading,
        isError,
        data: instrumentData,
    } = useQuery({
        queryKey: ["instrument", instumentCode],
        queryFn: () => fetchInstrumentByCode(instumentCode),
    });

    const setDataSource = (data) => {
        const dataSource = [];
        for (const [index, row] of data.entries()) {
            if (!row.hidden) {
                dataSource.push({
                    ...row,
                    key: instumentCode + index,
                });
            }
        }
        return dataSource;
    };

    const filterTable = (term) => {
        queryClient.setQueryData(["instrument", instumentCode], (data) =>
            data ? data.map((item) => {
                const hasSimbol = term ? item.simbolo?.toLowerCase().includes(term.toLowerCase()) : true;
                const hasDescription = term ? item.descripcion?.toLowerCase().includes(term.toLowerCase()) : true;
                const hasCategoryDescription = term ? item.categoriaDescripcion?.toLowerCase().includes(term.toLowerCase()) : true;
                const hasPlazo = term ? item.plazo?.toString().includes(term) : true;
                const hasOperaciones = term ? item.cantidadOperaciones?.toString().includes(term) : true;
                const hasTipo = term ? item.tipo?.toLowerCase().includes(term.toLowerCase()) : true;
                const hasSubasta = term ? item.subastaNumero?.toString().includes(term) : true;
                const hasSegmento = term ? item.segmento?.toLowerCase().includes(term.toLowerCase()) : true;
                return {
                    ...item,
                    hidden: !hasSimbol &&
                        !hasDescription &&
                        !hasCategoryDescription &&
                        !hasPlazo &&
                        !hasOperaciones &&
                        !hasTipo &&
                        !hasSubasta &&
                        !hasSegmento
                };
            }) : data,
        );
    };

    return (
        <>
            <BaseCard classNames={"cot-more-info-and-search"}>
                {instrumentProductPageUrl ? (
                    <div className="more-info">
                        <InfoCircleOutlined />
                        <a href={instrumentProductPageUrl} className="btn-link medium-text">
                            Más información de {instrumentName}
                        </a>
                    </div>
                ) : (
                    <i></i>
                )}

                <Search
                    className="--theme-light"
                    placeholder="Buscar"
                    style={{ maxWidth: 264 }}
                    onChange={(e) => filterTable(e.target.value)}
                    disabled={isError}
                />
            </BaseCard>

            {isLoading ? (
                <SkeletonTableTabs rows={10} withTabs={false} withSearchBar={false} />
            ) : isError ? (
                <Empty
                    className="empty-table pt-4"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No se han podido cargar los datos."
                />
            ) : (
                <>
                    <Table
                        className="cot-product-table"
                        dataSource={setDataSource(instrumentData)}
                        columns={renderColumns(locale)[instumentCode]}
                        showSorterTooltip={false}
                        onRow={(record) => {
                            return {
                                onClick: () => {
                                    switch (instumentCode) {
                                        case "cheques":
                                        case "pagares":
                                        case "cauciones":
                                            break;

                                        case 'fondos':
                                            if (!record.id) navigate("/error");
                                            navigate(`/${instumentCode}/${record.id}/`);
                                            break;

                                        default:
                                            if (!record.simbolo) navigate("/error");
                                            navigate(`/${instumentCode}/${record.simbolo}/`);
                                            break;
                                    }
                                },
                            };
                        }}
                        pagination={{
                            responsive: true,
                        }}
                        scroll={instumentCode === 'cheques' || instumentCode === 'pagares' ? {
                            scrollToFirstRowOnChange: true,
                            x: 940,
                        } : {}}
                    />
                    {
                        instumentCode.toLowerCase() === "fondos" ? (
                            <Flex justify="center" align="center" vertical style={{ gridColumn: "1/-1" }}>
                                <p className="small-text" style={{ letterSpacing: "0", color: "rgba(17, 17, 17, 60%)", marginBottom: "8px" }}>{__("Fuente provista por BYMA y CAFCI. Los valores de cuotapartes se actualizan en forma diaria, el resto de los datos son diferidos en aproximadamente 20 minutos. Salvo Error u Omisión (SEUO).")}</p>
                                <p className="small-text" style={{ letterSpacing: "0", color: "rgba(17, 17, 17, 60%)" }}>La variacion corresponde a la ultima semana corriente</p>
                            </Flex>
                        ) : 
                            <Flex justify="center" align="center" style={{ gridColumn: "1/-1" }}>
                                <p className="small-text" style={{ letterSpacing: "0", color: "rgba(17, 17, 17, 60%)" }}>{__("Fuente provista por BYMA y CAFCI. Los valores de cuotapartes se actualizan en forma diaria, el resto de los datos son diferidos en aproximadamente 20 minutos. Salvo Error u Omisión (SEUO).")}</p>
                            </Flex>
                    }
                </>
            )}
        </>
    );
}
